import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

export default function ContactsPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('service', formData.service);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5ps462hs78ip53701j0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#0A1628] to-[#1B5E4F]">
        <div className="max-w-[1400px] mx-auto px-8 text-center">
          <h1 className="text-7xl font-bold text-white mb-6">Свяжитесь с нами</h1>
          <p className="text-2xl text-[#D4AF37] font-light">
            Начните путь к успеху вашего бизнеса
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-[#F5F7FA]">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="grid grid-cols-2 gap-0 bg-white rounded-[48px] overflow-hidden shadow-[0_40px_100px_rgba(10,22,40,0.15)]">
            {/* Left Side - Contact Info */}
            <div className="bg-[#0A1628] p-16 flex flex-col justify-between">
              <div>
                <h2 className="text-5xl font-bold text-white mb-8">Начнем разговор</h2>
                
                <div className="space-y-8 mb-12">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-[#D4AF37] text-xl"></i>
                    </div>
                    <div>
                      <div className="text-[#D4AF37] text-sm font-medium mb-1">Адрес</div>
                      <div className="text-white font-light">Istanbul, Turkey</div>
                      <div className="text-white/60 text-sm">Главный офис</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-[#D4AF37] text-xl"></i>
                    </div>
                    <div>
                      <div className="text-[#D4AF37] text-sm font-medium mb-1">Телефон</div>
                      <div className="text-white font-light">+90 212 XXX XX XX</div>
                      <div className="text-white/60 text-sm">Пн-Пт: 9:00 - 18:00</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-[#D4AF37] text-xl"></i>
                    </div>
                    <div>
                      <div className="text-[#D4AF37] text-sm font-medium mb-1">Email</div>
                      <div className="text-white font-light">info@unitycore.com</div>
                      <div className="text-white/60 text-sm">Ответим в течение 24 часов</div>
                    </div>
                  </div>
                </div>

                <div className="rounded-[32px] overflow-hidden h-[300px]">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3009.7547842857!2d28.9783!3d41.0082!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDAwJzI5LjUiTiAyOMKwNTgnNDEuOSJF!5e0!3m2!1sen!2s!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="p-16">
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-8">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Ваше имя"
                    required
                    className="w-full bg-transparent border-b-2 border-gray-300 pb-4 text-[#0A1628] placeholder-gray-400 focus:border-[#D4AF37] outline-none transition-colors text-lg"
                  />
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email"
                    required
                    className="w-full bg-transparent border-b-2 border-gray-300 pb-4 text-[#0A1628] placeholder-gray-400 focus:border-[#D4AF37] outline-none transition-colors text-lg"
                  />
                </div>

                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Телефон"
                    required
                    className="w-full bg-transparent border-b-2 border-gray-300 pb-4 text-[#0A1628] placeholder-gray-400 focus:border-[#D4AF37] outline-none transition-colors text-lg"
                  />
                </div>

                <div>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border-b-2 border-gray-300 pb-4 text-[#0A1628] focus:border-[#D4AF37] outline-none transition-colors text-lg cursor-pointer"
                  >
                    <option value="">Выберите услугу</option>
                    <option value="financial">Финансовый консалтинг</option>
                    <option value="management">Управленческий консалтинг</option>
                    <option value="turkey">Бизнес в Турции</option>
                    <option value="investment">Инвестиционные стратегии</option>
                    <option value="scaling">Международное масштабирование</option>
                    <option value="other">Другое</option>
                  </select>
                </div>

                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Расскажите о вашем проекте"
                    rows={4}
                    maxLength={500}
                    required
                    className="w-full bg-transparent border-b-2 border-gray-300 pb-4 text-[#0A1628] placeholder-gray-400 focus:border-[#D4AF37] outline-none transition-colors text-lg resize-none"
                  ></textarea>
                  <div className="text-xs text-gray-400 mt-2 text-right">
                    {formData.message.length}/500 символов
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="consent"
                    required
                    className="mt-1 w-5 h-5 rounded border-gray-300 text-[#D4AF37] focus:ring-[#D4AF37] cursor-pointer"
                  />
                  <label htmlFor="consent" className="text-sm text-gray-600 font-light leading-relaxed cursor-pointer">
                    Я согласен на обработку персональных данных и получение информационных материалов
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-5 rounded-full bg-[#0A1628] text-white font-semibold text-lg whitespace-nowrap hover:bg-[#1B5E4F] transition-all hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-2xl text-green-800 text-center">
                    ✓ Спасибо! Ваша заявка успешно отправлена. Мы свяжемся с вами в ближайшее время.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-2xl text-red-800 text-center">
                    ✗ Произошла ошибка. Пожалуйста, попробуйте позже или свяжитесь с нами по телефону.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact Cards */}
      <section className="py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="text-center mb-20">
            <h2 className="text-6xl font-semibold text-[#0A1628] mb-6">Другие способы связи</h2>
            <p className="text-xl text-gray-600 font-light">Выберите удобный для вас канал коммуникации</p>
          </div>

          <div className="grid grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-whatsapp-line',
                title: 'WhatsApp',
                description: 'Быстрые ответы в мессенджере',
                action: 'Написать в WhatsApp',
                color: 'from-[#25D366] to-[#128C7E]'
              },
              {
                icon: 'ri-telegram-line',
                title: 'Telegram',
                description: 'Общайтесь в удобное время',
                action: 'Открыть Telegram',
                color: 'from-[#0088cc] to-[#0066aa]'
              },
              {
                icon: 'ri-calendar-line',
                title: 'Онлайн-встреча',
                description: 'Запланируйте видеоконсультацию',
                action: 'Выбрать время',
                color: 'from-[#D4AF37] to-[#F4D03F]'
              }
            ].map((item, i) => (
              <div key={i} className="bg-[#F5F7FA] rounded-[40px] p-10 text-center hover:shadow-[0_30px_80px_rgba(10,22,40,0.12)] transition-all hover:-translate-y-2">
                <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center mx-auto mb-6`}>
                  <i className={`${item.icon} text-4xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-semibold text-[#0A1628] mb-3">{item.title}</h3>
                <p className="text-gray-600 font-light mb-6">{item.description}</p>
                <button className="px-8 py-3 rounded-full bg-white text-[#0A1628] font-medium whitespace-nowrap hover:shadow-lg transition-all cursor-pointer">
                  {item.action}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-32 bg-[#F5F7FA]">
        <div className="max-w-[1200px] mx-auto px-8">
          <div className="text-center mb-20">
            <h2 className="text-6xl font-semibold text-[#0A1628] mb-6">Часто задаваемые вопросы</h2>
            <p className="text-xl text-gray-600 font-light">Ответы на популярные вопросы</p>
          </div>

          <div className="space-y-6">
            {[
              {
                question: 'Сколько стоят ваши услуги?',
                answer: 'Стоимость зависит от объема и сложности проекта. Мы предлагаем бесплатную первичную консультацию, на которой оценим ваши потребности и предложим оптимальное решение.'
              },
              {
                question: 'Как долго занимает регистрация компании в Турции?',
                answer: 'Процесс регистрации обычно занимает 5-7 рабочих дней. Это включает подготовку документов, регистрацию в торговой палате и получение налогового номера.'
              },
              {
                question: 'Работаете ли вы с малым бизнесом?',
                answer: 'Да, мы работаем с компаниями любого размера — от стартапов до крупных корпораций. Наши решения масштабируются под ваши потребности и бюджет.'
              },
              {
                question: 'Нужно ли мне приезжать в Турцию для открытия компании?',
                answer: 'В большинстве случаев достаточно одного визита для подписания документов. Мы можем организовать удаленное оформление с использованием нотариальных доверенностей.'
              },
              {
                question: 'Какие гарантии вы предоставляете?',
                answer: 'Мы гарантируем полную легальность всех операций и несем ответственность за качество наших услуг. Все наши специалисты имеют соответствующие лицензии и сертификаты.'
              }
            ].map((item, i) => (
              <details key={i} className="bg-white rounded-[32px] p-8 shadow-[0_10px_40px_rgba(10,22,40,0.06)] hover:shadow-[0_20px_60px_rgba(10,22,40,0.1)] transition-all group">
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <h3 className="text-xl font-semibold text-[#0A1628] pr-8">{item.question}</h3>
                  <i className="ri-arrow-down-s-line text-2xl text-[#D4AF37] group-open:rotate-180 transition-transform flex-shrink-0"></i>
                </summary>
                <p className="text-gray-600 font-light leading-relaxed mt-6 pt-6 border-t border-gray-200">
                  {item.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#0A1628] to-[#1B5E4F] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-96 h-96 bg-[#D4AF37] rounded-full blur-[100px]"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#D4AF37] rounded-full blur-[100px]"></div>
        </div>
        <div className="max-w-[1400px] mx-auto px-8 text-center relative">
          <h2 className="text-5xl font-bold text-white mb-6">Готовы начать?</h2>
          <p className="text-xl text-gray-300 font-light mb-10">
            Запишитесь на бесплатную консультацию и получите персональный план развития
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/financial-consulting" className="inline-flex items-center gap-3 px-10 py-4 rounded-full bg-[#D4AF37] text-[#0A1628] font-semibold whitespace-nowrap hover:shadow-2xl transition-all hover:-translate-y-1 cursor-pointer">
              Финансовые услуги
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link to="/management-consulting" className="inline-flex items-center gap-3 px-10 py-4 rounded-full border-2 border-white text-white font-semibold whitespace-nowrap hover:bg-white/10 transition-all cursor-pointer">
              Управленческий консалтинг
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
